// Sprint-101b — State-level serviceability banner.
// Renders idle/loading/serviceable/unserviceable/error states.
// On unserviceable: shows "Notify Me" button that opens WaitlistModal.
import { useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import WaitlistModal from "./WaitlistModal";
import ServingStatesModal from "./ServingStatesModal";

export default function StateServiceabilityBanner({
  sv,           // shape from useStateServiceability
  pincode,
  prefillEmail = "",
  source = "booking_modal",
  className = "",
}) {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [statesModalOpen, setStatesModalOpen] = useState(false);

  // Idle: nothing checked yet → render nothing.
  if (sv == null || (sv.serviceable == null && !sv.loading && !sv.error)) return null;

  if (sv.loading) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 ${className}`}
        data-testid="state-sv-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking pickup availability…
      </div>
    );
  }

  if (sv.error) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 ${className}`}
        data-testid="state-sv-error"
      >
        <AlertCircle className="h-4 w-4" />
        Couldn&apos;t verify — try again.
      </div>
    );
  }

  if (sv.serviceable) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 ${className}`}
        data-testid="state-sv-ok"
      >
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <span>
          Pickup available in {sv.state || "your state"}
          {sv.activeHubCount != null && sv.activeHubCount > 0
            ? <span className="text-emerald-600/80"> · {sv.activeHubCount} active hub{sv.activeHubCount > 1 ? "s" : ""}</span>
            : null}
        </span>
      </div>
    );
  }

  // Unserviceable
  return (
    <>
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 ${className}`}
        data-testid="state-sv-blocked"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MapPin className="h-4 w-4 text-rose-600 shrink-0" />
          <span className="truncate">
            {sv.message || "Pickup not available in this state yet"}
            {sv.state ? <span className="text-rose-600/80"> · {sv.state}</span> : null}
          </span>
        </div>
        <Button
          size="sm"
          className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
          onClick={() => setWaitlistOpen(true)}
          data-testid="state-sv-notify-me-btn"
        >
          Notify Me
        </Button>
      </div>
      <button
        type="button"
        onClick={() => setStatesModalOpen(true)}
        className="mt-1 text-xs text-slate-500 hover:text-slate-700 hover:underline self-start"
        data-testid="state-sv-where-link"
      >
        See where PickNGo currently operates →
      </button>

      <ServingStatesModal open={statesModalOpen} onOpenChange={setStatesModalOpen} />

      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        prefillEmail={prefillEmail}
        prefillPincode={pincode || ""}
        prefillState={sv.state || ""}
        source={source}
      />
    </>
  );
}
